import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import SEO from '@/components/feature/SEO';
import AdminPageEditBar from '@/components/feature/AdminPageEditBar';
import CTASection, { CTASectionData, defaultCTAData } from '@/pages/home/components/CTASection';
import AboutHero, { AboutHeroData } from './components/AboutHero';
import CathyStory, { CathyStoryData } from './components/CathyStory';
import MissionVision, { MissionVisionData } from './components/MissionVision';

export interface AboutPageContent {
  hero: AboutHeroData;
  story: CathyStoryData;
  missionVision: MissionVisionData;
  cta: CTASectionData;
}

export const defaultAboutContentES: AboutPageContent = {
  hero: {
    badge: 'Nuestra Historia',
    titlePrefix: 'Sobre ',
    titleHighlight: 'Nosotros',
    subtitle: 'Conoce la historia detrás de Smart Business Digital Solutions y la misión que nos impulsa a empoderar emprendedores latinos en todo el mundo.'
  },
  story: {
    photoBadge: 'Fundadora & CEO',
    badge: 'Mi Historia',
    title: 'De Emprendedora a',
    titleHighlight: 'Mentora Digital',
    name: 'Cathy Roldán',
    company: 'Smart Business Digital Solutions LLC',
    photoUrl: 'https://readdy.ai/api/search-image?query=confident%20latina%20entrepreneur%20businesswoman%20digital%20mentor%20professional%20photo%20studio%20purple%20blue%20lighting%20elegant%20modern%20background%20empowering&width=500&height=600&seq=cathy2&orientation=portrait',
    p1: 'Cathy Roldán es una emprendedora, mentora digital y educadora apasionada por empoderar a la comunidad latina. Con raíces profundas en la cultura hispana, Cathy entendió desde temprano que el acceso a la educación digital podía cambiar vidas.',
    p2: 'Comenzó su camino en el marketing digital de forma autodidacta, aprendiendo cada herramienta, cada estrategia y cada plataforma disponible. Lo que empezó como una necesidad personal se convirtió en una misión: democratizar el conocimiento digital para emprendedores latinos.',
    p3: 'Hoy, a través de Smart Business Digital Solutions LLC y Smart Business Academy, Cathy ha ayudado a más de 500 estudiantes y 200 clientes a transformar sus negocios, aumentar sus ingresos y construir marcas poderosas en el mundo digital.',
    p4: 'Su enfoque combina estrategia de marketing digital, herramientas de inteligencia artificial, automatización de negocios y educación práctica. Todo diseñado para que cualquier emprendedor, sin importar su nivel técnico, pueda dominar el mundo online.',
    trajectoryTitle: 'Trayectoria',
    milestones: [
      { year: '2018', title: 'El Comienzo', desc: 'Cathy inicia su carrera en marketing digital, aprendiendo de forma autodidacta las estrategias de redes sociales y negocios online.' },
      { year: '2019', title: 'Primeros Clientes', desc: 'Comienza a trabajar con pequeños negocios latinos, ayudándoles a establecer su presencia digital y aumentar sus ventas.' },
      { year: '2021', title: 'Smart Business Nace', desc: 'Funda Smart Business Digital Solutions LLC, consolidando su equipo y expandiendo los servicios de marketing digital y automatización.' },
      { year: '2022', title: 'Smart Business Academy', desc: 'Lanza la academia online para emprendedores latinos, democratizando el acceso a educación digital de calidad.' },
      { year: '2024', title: 'Impacto Global', desc: 'Más de 500 estudiantes y 200 clientes en toda Latinoamérica y Estados Unidos, con presencia en múltiples plataformas digitales.' }
    ]
  },
  missionVision: {
    mission: {
      label: 'Misión',
      title: 'Empoderar Emprendedores Latinos',
      text: 'Nuestra misión es empoderar a emprendedores y dueños de negocios latinos en todo el mundo, brindándoles las herramientas, estrategias y conocimientos de marketing digital e inteligencia artificial necesarios para construir negocios rentables y sostenibles en la era digital.'
    },
    vision: {
      label: 'Visión',
      title: 'Líderes del Mundo Digital Latino',
      text: 'Ser la empresa de marketing digital y educación online más influyente para la comunidad latina, creando un ecosistema donde cada emprendedor hispano tenga acceso a las mismas oportunidades digitales que los líderes globales del negocio.'
    },
    valuesTitle: 'Nuestros Valores',
    valuesSub: 'Lo Que Nos',
    valuesHighlight: 'Define',
    values: [
      { icon: 'ri-heart-line', title: 'Comunidad', desc: 'Creemos en el poder de la comunidad latina y trabajamos para fortalecer cada negocio que forma parte de ella.', color: '#FF2DAA' },
      { icon: 'ri-lightbulb-line', title: 'Innovación', desc: 'Adoptamos las últimas tecnologías de IA y marketing digital para mantener a nuestros clientes a la vanguardia.', color: '#8c52ff' },
      { icon: 'ri-shield-check-line', title: 'Integridad', desc: 'Trabajamos con transparencia y honestidad, priorizando siempre el éxito real de nuestros clientes.', color: '#00c2ff' },
      { icon: 'ri-rocket-line', title: 'Resultados', desc: 'Nos enfocamos en métricas reales: más seguidores, más ventas, más ingresos para tu negocio.', color: '#FF2DAA' }
    ],
    experienceTitle: 'Experiencia & Especialidades',
    areas: [
      { area: 'Marketing Digital', skills: ['Social Media Strategy', 'Content Marketing', 'SEO & SEM', 'Paid Advertising', 'Analytics'], color: '#8c52ff' },
      { area: 'Tecnología & IA', skills: ['AI Automation Tools', 'Sales Funnels', 'Email Marketing', 'CRM Systems', 'Chatbots'], color: '#00c2ff' },
      { area: 'Educación & Coaching', skills: ['Online Courses', 'Group Coaching', '1:1 Mentoring', 'Workshops', 'Community Building'], color: '#FF2DAA' }
    ]
  },
  cta: defaultCTAData
};

export const defaultAboutContentEN: AboutPageContent = {
  hero: {
    badge: 'Our Story',
    titlePrefix: 'About ',
    titleHighlight: 'Us',
    subtitle: 'Meet the story behind Smart Business Digital Solutions and the mission that drives us to empower Latino entrepreneurs worldwide.'
  },
  story: {
    photoBadge: 'Founder & CEO',
    badge: 'My Story',
    title: 'From Entrepreneur to',
    titleHighlight: 'Digital Mentor',
    name: 'Cathy Roldán',
    company: 'Smart Business Digital Solutions LLC',
    photoUrl: 'https://readdy.ai/api/search-image?query=confident%20latina%20entrepreneur%20businesswoman%20digital%20mentor%20professional%20photo%20studio%20purple%20blue%20lighting%20elegant%20modern%20background%20empowering&width=500&height=600&seq=cathy2&orientation=portrait',
    p1: 'Cathy Roldán is an entrepreneur, digital mentor, and educator passionate about empowering the Latino community. With deep roots in Hispanic culture, Cathy understood early on that access to digital education could change lives.',
    p2: 'She began her journey in digital marketing as a self-taught professional, mastering every tool, strategy, and platform available. What started as a personal need soon evolved into a mission: to democratize digital knowledge for Latino entrepreneurs.',
    p3: 'Today, through Smart Business Digital Solutions LLC and Smart Business Academy, Cathy has helped over 500 students and 200 clients transform their businesses, increase their income, and build powerful brands in the digital world.',
    p4: 'Her approach combines digital marketing strategy, artificial intelligence tools, business automation, and practical education. Everything is designed so that any entrepreneur, regardless of their technical level, can master the online world.',
    trajectoryTitle: 'Trajectory',
    milestones: [
      { year: '2018', title: 'The Beginning', desc: 'Cathy starts her career in digital marketing, teaching herself social media strategies and online business.' },
      { year: '2019', title: 'First Clients', desc: 'Starts working with small Latino businesses, helping them establish their digital presence and increase sales.' },
      { year: '2021', title: 'Smart Business is Born', desc: 'Foundations of Smart Business Digital Solutions LLC, consolidating her team and expanding digital marketing and automation services.' },
      { year: '2022', title: 'Smart Business Academy', desc: 'Launches the online academy for Latino entrepreneurs, democratizing access to quality digital education.' },
      { year: '2024', title: 'Global Impact', desc: 'Over 500 students and 200 clients across Latin America and the United States, with a presence on multiple digital platforms.' }
    ]
  },
  missionVision: {
    mission: {
      label: 'Mission',
      title: 'Empowering Latino Entrepreneurs',
      text: 'Our mission is to empower Latino entrepreneurs and business owners worldwide by providing them with the tools, strategies, and knowledge in digital marketing and artificial intelligence necessary to build profitable and sustainable businesses in the digital age.'
    },
    vision: {
      label: 'Vision',
      title: 'Leaders in the Latino Digital World',
      text: 'To be the most influential digital marketing and online education company for the Latino community, creating an ecosystem where every Hispanic entrepreneur has access to the same digital opportunities as global business leaders.'
    },
    valuesTitle: 'Our Values',
    valuesSub: 'What ',
    valuesHighlight: 'Defines Us',
    values: [
      { icon: 'ri-heart-line', title: 'Community', desc: 'We believe in the power of the Latino community and work to strengthen every business that is a part of it.', color: '#FF2DAA' },
      { icon: 'ri-lightbulb-line', title: 'Innovation', desc: 'We adopt the latest AI and digital marketing technologies to keep our clients at the forefront.', color: '#8c52ff' },
      { icon: 'ri-shield-check-line', title: 'Integrity', desc: 'We work with transparency and honesty, always prioritizing our clients\' true success.', color: '#00c2ff' },
      { icon: 'ri-rocket-line', title: 'Results', desc: 'We focus on real metrics: more followers, more sales, more income for your business.', color: '#FF2DAA' }
    ],
    experienceTitle: 'Experience & Specialties',
    areas: [
      { area: 'Digital Marketing', skills: ['Social Media Strategy', 'Content Marketing', 'SEO & SEM', 'Paid Advertising', 'Analytics'], color: '#8c52ff' },
      { area: 'Technology & AI', skills: ['AI Automation Tools', 'Sales Funnels', 'Email Marketing', 'CRM Systems', 'Chatbots'], color: '#00c2ff' },
      { area: 'Education & Coaching', skills: ['Online Courses', 'Group Coaching', '1:1 Mentoring', 'Workshops', 'Community Building'], color: '#FF2DAA' }
    ]
  },
  cta: {
    badge: 'TRANSFORM YOUR BUSINESS',
    titlePrefix: 'Ready to transform your ',
    titleHighlight: 'digital presence?',
    subtitle: 'Discover how our comprehensive digital marketing, branding, and AI automation solutions can scale your business.',
    btnAcademyText: 'Explore Academy',
    btnAcademyLink: 'https://smartbusiness.academy',
    btnConsultText: 'Book Consultation',
    btnConsultLink: '/contact',
    btnServicesText: 'View Services',
    btnServicesLink: '/services',
    trustIndicators: [
      { icon: 'ri-shield-check-line', text: '100% Customized' },
      { icon: 'ri-customer-service-line', text: 'Ongoing Support' },
      { icon: 'ri-global-line', text: 'Global Focus' }
    ]
  }
};

const AboutPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'es';
  const docId = lang === 'en' ? 'about_page_en' : 'about_page';
  const defaultContent = lang === 'en' ? defaultAboutContentEN : defaultAboutContentES;

  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [content, setContent] = useState<AboutPageContent>(defaultContent);
  const [initialContent, setInitialContent] = useState<AboutPageContent>(defaultContent);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedNotification, setSavedNotification] = useState(false);

  useEffect(() => {
    // Reset immediately to the target language defaults while fetching
    setContent(defaultContent);
    setInitialContent(defaultContent);

    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'landing_content', docId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<AboutPageContent>;
          const merged: AboutPageContent = {
            hero: { ...defaultContent.hero, ...(data.hero || {}) },
            story: { 
              ...defaultContent.story, 
              ...(data.story || {}),
              badge: data.story?.badge || defaultContent.story.badge,
              title: data.story?.title || defaultContent.story.title,
              titleHighlight: data.story?.titleHighlight || defaultContent.story.titleHighlight,
              p1: data.story?.p1 || defaultContent.story.p1,
              p2: data.story?.p2 || defaultContent.story.p2,
              p3: data.story?.p3 || defaultContent.story.p3,
              p4: data.story?.p4 || defaultContent.story.p4,
              milestones: data.story?.milestones || defaultContent.story.milestones 
            },
            missionVision: { 
              ...defaultContent.missionVision, 
              ...(data.missionVision || {}),
              values: data.missionVision?.values || defaultContent.missionVision.values,
              areas: data.missionVision?.areas || defaultContent.missionVision.areas
            },
            cta: { 
              ...defaultContent.cta, 
              ...(data.cta || {}),
              trustIndicators: data.cta?.trustIndicators || defaultContent.cta.trustIndicators
            }
          };
          setContent(merged);
          setInitialContent(merged);
        }
      } catch (error) {
        console.error('Error fetching about page content:', error);
      }
    };

    fetchContent();
  }, [lang, docId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'landing_content', docId);
      await setDoc(docRef, content);
      setInitialContent(content);
      setIsEditing(false);
      setSavedNotification(true);
      setTimeout(() => setSavedNotification(false), 3500);
    } catch (error) {
      console.error('Error saving about page content:', error);
      alert(lang === 'en' ? 'Error saving changes to Firestore.' : 'Error al guardar los cambios en Firestore.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setContent(initialContent);
    setIsEditing(false);
  };

  return (
    <main className="relative">
      <SEO 
        title={lang === 'en' ? "About Us | Sbdigisolutions" : "Sobre Nosotros | Sbdigisolutions"} 
        description={lang === 'en' ? "Learn the story behind Sbdigisolutions, our mission, vision, and team of experts." : "Conoce la historia detrás de Sbdigisolutions, nuestra misión, visión y a nuestro equipo de expertos en marketing digital."} 
      />

      <AdminPageEditBar
        isAdmin={isAdmin}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
        pageName={lang === 'en' ? 'About Us (EN)' : 'Sobre Nosotros (ES)'}
      />

      {savedNotification && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 bg-emerald-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-2xl flex items-center gap-2 animate-bounce">
          <i className="ri-checkbox-circle-line text-lg" />
          <span>{lang === 'en' ? 'About Us page saved successfully!' : '¡Página Sobre Nosotros guardada correctamente!'}</span>
        </div>
      )}

      <AboutHero
        data={content.hero}
        isEditing={isEditing}
        onChange={(hero) => setContent({ ...content, hero })}
      />

      <CathyStory
        data={content.story}
        isEditing={isEditing}
        onChange={(story) => setContent({ ...content, story })}
      />

      <MissionVision
        data={content.missionVision}
        isEditing={isEditing}
        onChange={(missionVision) => setContent({ ...content, missionVision })}
      />

      <CTASection
        data={content.cta}
        isEditing={isEditing}
        onChange={(cta) => setContent({ ...content, cta })}
      />
    </main>
  );
};

export default AboutPage;
